import { getFileUrlFromDriveInfo, getSessionIdFromDriveInfo, checkGraphResponse, getSessionForAccessInfo } from "./GraphMain";
import { accessInfoType } from "../utils/classInfos";
import { getElementAppInfo } from "./graphAppInfoElementFunctions";
import { getNormalizedElementTypeId } from "../utils/helper";
import { getHelperTables, getWorksheetUrlSection } from "./helper";
import store from "./StoreRoot";

export async function getChartDetailInfo(
  accessInfo: accessInfoType,
  chartId: string,
  worksheetId: string,
  imageWidth: number = 2000
) {
  const sessionInfo  = await getSessionForAccessInfo(accessInfo);
  // check the info
  if (!sessionInfo || !sessionInfo.sessionId) return null;
  const {client, sessionId, fileUrl} = sessionInfo;
  
  const chartUrl =
    fileUrl +
    "/workbook/"+getWorksheetUrlSection(worksheetId)+"/charts('" +
    chartId +
    "')";
  // get the detail information
  const $batch = {
    requests: [
      {
        url: chartUrl + "/image(width=" + imageWidth + ",height=0,fittingMode='fit')",
        method: "GET",
        id: "IMAGE",
        headers: {
          "workbook-session-id": sessionId
        }
      },
      {
        url: chartUrl + "/title",
        method: "GET",
        id: "TITLE",
        headers: {
          "workbook-session-id": sessionId
        }
      },
      {
        url: chartUrl,
        method: "GET",
        id: "INFO",
        headers: {
          "workbook-session-id": sessionId
        }
      },
      {
        url: fileUrl + "/workbook/tables?$select=name",
        method: "GET",
        id: "TABLES",
        headers: {
          "workbook-session-id": sessionId
        }
      },
    ],
  };
  // RUN BATCH
  let res = await client
    .api("/$batch")
    .post($batch);

  checkGraphResponse(res, "getChartDetailInfo");

  // RES
  let chartImage = res.responses[res.responses.findIndex((x) => x.id === "IMAGE")]?.body?.value;
  let chartTitle = res.responses[res.responses.findIndex((x) => x.id === "TITLE")]?.body?.text;
  let tableTables = res.responses[res.responses.findIndex((x) => x.id === "TABLES")]?.body;
  let chartInfo = res.responses[res.responses.findIndex((x) => x.id === "INFO")]?.body;
  // helper tables
  const helperTables = getHelperTables(tableTables?.value);
  const hasHelperTables = helperTables.length > 0;
  // get the app info
  let appInfo = await getElementAppInfo(accessInfo, chartId, "chart", sessionId, fileUrl, client, helperTables);    

  return { chartId, chartTitle, chartImage, chartInfo, appInfo, accessInfo, hasHelperTables };
}

export async function getWorkbookChartsBatch(
  client: any,
  accessInfo: any,
  workSheets: any[],
  documentSessionId: string,
  detailsReadyCallback: any
) {
  if (!workSheets) return;
  
  const maxBatchCount = 18;
  const fileUrl = getFileUrlFromDriveInfo(accessInfo.driveItemInfo);
  const sessionId = documentSessionId ?? await getSessionIdFromDriveInfo(accessInfo.driveItemInfo);
  let batchRequests = [];
  let chartList = [];
  workSheets.forEach(workSheet => {
    if (batchRequests.length < maxBatchCount) {
      batchRequests.push({
        url:
          fileUrl +
          "/workbook/" + getWorksheetUrlSection(workSheet.id) + "/charts",
        method: "GET",
        id: workSheet.id,
        headers: {
          "workbook-session-id": sessionId
        }
      });
    }
  })

  const $batch = {
    requests: batchRequests,
  };

  let res = await client
    .api("/$batch")
    .post($batch);

  checkGraphResponse(res, "getWorkbookChartsBatch");  

  // get the charts for every worksheets
  workSheets.forEach(workSheet => {
    let tempCharts = res.responses[res.responses.findIndex((x) => x.id === workSheet.id)];
    // go through all the charts in that worksheet
    if (checkGraphResponse(tempCharts, "getWorkbookFullInfo.worksheetCharts")) {
      tempCharts.body.value.forEach(chart => {
        chartList.push({
          id: chart.id,
          elementId: getNormalizedElementTypeId(chart.id, "chart"),
          name: chart.name,
          worksheet: workSheet.name,
          worksheetId: workSheet.id,
          width: chart.width,
          height: chart.height,
          type: "chart",
          fulltext: "chart, ",
          caption: chart.name,
          thumbnail: null,
          propertiesLoaded: false
        });
      });
    }
  });
  // check the element limit
  chartList.forEach((chart, index) => {
    chart.needToUpgrade = (index+1 > 50);
  })

  // look for titles and thumbnails
  if (chartList.length > 0) {
    // wait for the batch
    let chartsProcessed = await getWorkbookChartsBatchDetails(client, accessInfo, sessionId, chartList, detailsReadyCallback);
    return chartsProcessed;

    // if (detailsReadyCallback) {
    //   // dont wait - the details will be send to callback
    //   getWorkbookChartsBatchDetails(client, accessInfo, sessionId, chartList, detailsReadyCallback);
    //   return chartList;
    // } else {
    //   // wait for the batch
    //   let chartsProcessed = await getWorkbookChartsBatchDetails(client, accessInfo, sessionId, chartList, null);
    //   return chartsProcessed;
    // }
  }

  return [];
}

async function getWorkbookChartsBatchDetails(
  client: any,
  accessInfo: any,
  sessionId: string,
  chartList: any[],
  detailsReadyCallback: any
) {
  if (!chartList) return;

  // look for titles and thumbnails
  if (chartList.length > 0) {
    (chartList.length > 7) && store.main.setLoadingString(`loading ${chartList.length} charts... hold out for a moment`);
    let chartsToProcess = [...chartList];
    let chartsProcessed = [];

    do {
      let chartList_part: any = await getChartsDetailInfoBatch(
        client,
        accessInfo,
        sessionId,
        chartsToProcess,
        store.main.chartThumbnailSize
      );
      chartsToProcess = [];
      // check for processed entries
      // eslint-disable-next-line no-loop-func
      chartList_part.items.forEach((chart) => {
        if (chart && chart.hasErrors === false) {
          if (chart.propertiesLoaded === false) {
            chartsToProcess.push({...chart});
          }
          if (chart.propertiesLoaded === true) {
            chartsProcessed.push({...chart});
          }
        }
      });
    } while (chartsToProcess.length > 0);

    if (detailsReadyCallback) {
      detailsReadyCallback(accessInfo, chartsProcessed);
    }

    return chartsProcessed;
  }

  return [];
}


export async function getChartsDetailInfoBatch(
  client: any,
  accessInfo: any,
  sessionId: string,
  charts: any[],
  imageWidth: number = 600
) {
  const maxBatchCount = 18;
  const fileUrl = getFileUrlFromDriveInfo(accessInfo.driveItemInfo);
  let tempRequests = [];
  let withThumbnails = accessInfo.driveItemFlags?.fetchChartThumbnails && true;

  charts.forEach(chart => {
    if (chart.propertiesLoaded === false && tempRequests.length <= maxBatchCount) {
      if (chart.needToUpgrade !== true) {
        // title request
        // tempRequests.push({
        //   url:
        //     fileUrl +
        //     "/workbook/worksheets('" +
        //     chart.worksheet +
        //     "')/charts('" +
        //     chart.name +
        //     "')/title",
        //   method: "GET",
        //   id: chart.id + "-title",
        //   headers: {
        //     "workbook-session-id": sessionId
        //   }
        // });
        // thumbnail request
        withThumbnails && tempRequests.push({
          url:
            fileUrl +
            "/workbook/" + getWorksheetUrlSection(chart.worksheet) + "/charts/" +
            chart.name +
            "/image(width=" +
            imageWidth +
            ",height=0,fittingMode='fit')",
          method: "GET",
          id: chart.id + "-thumbnail",
          headers: {
            "workbook-session-id": sessionId
          }
        });
      }
    }
  })
  let res = null;
  if (tempRequests.length > 0) {
    const $batch = {
      requests: tempRequests,
    };
    res = await client
      .api("/$batch")
      .post($batch);
  }

  checkGraphResponse(res, "getChartsDetailInfoBatch");  

  // get the chart detail
  charts.forEach(chart => {
    try {
      let chartStatus;
      if (!chart.needToUpgrade && res) {
        let tempTitle =
          res.responses[
            res.responses.findIndex((x) => x.id === chart.id + "-title")
          ];
        let tempThumbnail = withThumbnails &&
          res.responses[
            res.responses.findIndex((x) => x.id === chart.id + "-thumbnail")
          ];
        chart.caption = chart.name;
        // check the status
        chartStatus = tempThumbnail?.status;
        if (chartStatus === 500) {
          console.log("ERROR getting chart thumbnail", chart);
        } else {
          if (checkGraphResponse(tempTitle, "getChartsDetailInfoBatch.chartTitle")) {
            chart.caption = tempTitle.body?.text ? tempTitle.body?.text : chart.name;
          }
          if (withThumbnails) { 
            if (checkGraphResponse(tempThumbnail, "getChartsDetailInfoBatch.chartThumbnail")) {
              chart.thumbnail = tempThumbnail.body?.value;
            }
          }
        }
        chart.fulltext = chart.fulltext + chart.caption;
      }
      chart.propertiesLoaded = chart.needToUpgrade || !withThumbnails || (chart.thumbnail?.length > 0);
      chart.hasErrors = chartStatus === 500 || chartStatus === 429;
    } catch (err) {
      chart.hasErrors = true;
      console.log("ERROR response", err);
    }
  })

  return {
    items: charts,
  };
}
